import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

/**
 * Inline editor for a payer's per-modifier override rules.
 *
 * Each rule says "when the system would bill `from_modifier` for this
 * payer (optionally only for the listed CPTs), bill `to_modifier`
 * instead (or nothing if blank)". Rules fire in order; the output of
 * rule N feeds rule N+1, so chains like SA→HO→AH are supported.
 *
 * Backend contract: `insurances.modifier_rules: List[Rule]` where
 * Rule = { from_modifier, to_modifier, cpt_codes?, notes? }.
 *
 * This component is intentionally NOT added to App.js — it lives here
 * so future enhancements (bulk import, rule templates, audit history)
 * have somewhere to grow without touching the monolith.
 */
export const PayerModifierRulesEditor = ({ rules, onChange }) => {
  const list = Array.isArray(rules) ? rules : [];
  const tid = 'payer-modifier-rule';

  const update = (idx, patch) => {
    const next = list.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };

  const remove = (idx) => {
    onChange(list.filter((_, i) => i !== idx));
  };

  const add = () => {
    onChange([...list, { from_modifier: '', to_modifier: '', cpt_codes: [], notes: '' }]);
  };

  return (
    <div className="space-y-3" data-testid={`${tid}-editor`}>
      {list.length === 0 && (
        <p className="text-xs text-gray-500 italic" data-testid={`${tid}-empty`}>
          No overrides. This payer uses the standard credential-based modifiers.
        </p>
      )}

      {list.map((rule, idx) => (
        <div
          key={idx}
          className="p-3 border border-gray-200 rounded-md bg-gray-50 space-y-2"
          data-testid={`${tid}-row-${idx}`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <Label htmlFor={`${tid}-from-${idx}`} className="text-xs">When:</Label>
              <Input
                id={`${tid}-from-${idx}`}
                data-testid={`${tid}-from-${idx}`}
                value={rule.from_modifier || ''}
                onChange={(e) => update(idx, { from_modifier: e.target.value.toUpperCase().trim() })}
                placeholder="SA"
                className="w-20 font-mono"
                maxLength={3}
              />
            </div>
            <span className="text-gray-500">→</span>
            <div className="flex items-center gap-1">
              <Label htmlFor={`${tid}-to-${idx}`} className="text-xs">Bill as:</Label>
              <Input
                id={`${tid}-to-${idx}`}
                data-testid={`${tid}-to-${idx}`}
                value={rule.to_modifier || ''}
                onChange={(e) => update(idx, { to_modifier: e.target.value.toUpperCase().trim() })}
                placeholder="HO"
                className="w-20 font-mono"
                maxLength={3}
              />
              <span className="text-[10px] text-gray-500 italic">(blank = remove)</span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => remove(idx)}
              data-testid={`${tid}-remove-${idx}`}
              className="ml-auto text-red-600 hover:bg-red-50"
              aria-label="Remove rule"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          <div>
            <Label htmlFor={`${tid}-cpts-${idx}`} className="text-xs text-gray-600">
              Limit to CPT codes (optional)
            </Label>
            <Input
              id={`${tid}-cpts-${idx}`}
              data-testid={`${tid}-cpts-${idx}`}
              value={(rule.cpt_codes || []).join(', ')}
              onChange={(e) =>
                update(idx, {
                  cpt_codes: e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
              placeholder="90834, 90837 (leave blank for all CPTs)"
              className="font-mono text-sm"
            />
          </div>

          <div>
            <Label htmlFor={`${tid}-notes-${idx}`} className="text-xs text-gray-600">
              Notes (optional)
            </Label>
            <Input
              id={`${tid}-notes-${idx}`}
              data-testid={`${tid}-notes-${idx}`}
              value={rule.notes || ''}
              onChange={(e) => update(idx, { notes: e.target.value })}
              placeholder="e.g. BCBS prefers HO for psychotherapy"
              className="text-sm"
            />
          </div>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={add}
        data-testid={`${tid}-add`}
      >
        <Plus className="h-3 w-3 mr-1" /> Add modifier rule
      </Button>
    </div>
  );
};

export default PayerModifierRulesEditor;
